import { ReactElement, Fragment } from 'react';
import { useApiEffect, useApiGetter, UserService } from '@localstack/services';
import { ButtonWithDialog } from '@localstack/ui';
import { Typography } from '@mui/material';

import { StripeCardForm } from '~/components';

import { Divider, GridComponent } from '../utils';

export const PaymentMethod = (): ReactElement => {
  const { data: cards, isLoading: areCardsRetrieving } = useApiGetter(UserService, 'listCreditCards', [], {
    defaultValue: [],
  });

  const {
    attachCreditCard,
    removeCreditCard,
    isLoading: isCardMutating,
  } = useApiEffect(UserService, ['attachCreditCard', 'removeCreditCard'], { revalidate: ['listCreditCards'] });

  const areCardsLoading = areCardsRetrieving || isCardMutating;

  if (areCardsLoading) {
    return (
      <GridComponent columns="1fr">
        <Typography color="text.tertiary">Fetching your card details...</Typography>
      </GridComponent>
    );
  }

  if (!areCardsLoading && cards?.length === 0) {
    return (
      <GridComponent columns="1fr max-content">
        <Typography color="text.tertiary">There are no stored payment methods.</Typography>
        <ButtonWithDialog
          componentType="Button"
          dialogTitle="Add Payment Method"
          dialogContent={
            <StripeCardForm
              defaultCurrency="usd"
              loading={isCardMutating}
              onSaveCard={async (token, currency) => {
                if (!currency) {
                  return;
                }
                await attachCreditCard({ token, currency });
              }}
            />
          }
          hideDialogActions
          showCloseButton
        >
          Add
        </ButtonWithDialog>
      </GridComponent>
    );
  }

  return (
    <GridComponent columns="1fr max-content">
      {cards?.map((card, index) => (
        <Fragment key={card.id}>
          <Typography color="text.secondary">
            {card.brand} card ending in {card.last4} ({card.currency?.toUpperCase()})
          </Typography>
          <ButtonWithDialog
            componentType="Button"
            dialogTitle="Removing Payment Method"
            dialogContent={`Are you sure you want to remove this credit card (${card.brand} ****${card.last4})?
                                    Removing this credit card will also remove it from all current subscriptions.`}
            confirmButtonText="Remove Card"
            confirmButtonProps={{ color: 'error' }}
            onClick={() => removeCreditCard(card.id, card.currency as string)}
          >
            Remove
          </ButtonWithDialog>
          {index < cards?.length - 1 && <Divider />}
        </Fragment>
      ))}
    </GridComponent>
  );
};
